import { useEffect, useMemo, useState } from "react";

import {
  useGetFollowsQuery,
  type FollowUser,
} from "../../app/services/follows";
import {
  useGetRoleUsersQuery,
  type RoleUserItem,
} from "../../app/services/roles";
import type { AccountRole } from "../../app/services/accounts";
// @ts-expect-error - JS のため型定義なし
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";

/**
 * 1 リクエストあたりの取得件数。BE の `_UserRelationPagination.max_limit` と同値で、
 * これより大きい値を送ってもサーバー側で 100 にクランプされる。
 */
const PAGE_LIMIT = 100;

/**
 * 全ページ取得の安全弁。候補の母集団（フォロー中 / ロール該当者）は数百人規模を
 * 想定しているが、それを超えて増えたときにリクエストが際限なく続かないよう上限を
 * 設ける。ここで打ち切った場合は `reachedLimit` が立つ。
 */
const MAX_USERS = 2000;

/**
 * オファーの送信先候補に出さないアカウント種別（受託しないロール）。
 * 管理者 / クライアント / マネージャーはフォロー中でもカードに表示しない。
 */
const NON_RECIPIENT_ROLES = new Set<AccountRole>([
  "ADMIN",
  "CLIENT",
  "MANAGER",
]);

/** ページング API（`/api/me/follows`・`/api/roles/<id>/users`）共通のレスポンス形。 */
export interface UsersPage<T> {
  users: T[];
  count: number;
  limit: number;
  offset: number;
  has_next: boolean;
}

/** 全ページ取得の進行状態。`key` が変わったら最初のページからやり直す。 */
export interface PagingState<T> {
  key: string;
  /** 次に要求するページの offset。 */
  offset: number;
  users: T[];
  /** `has_next` が尽きた / エラーで打ち切った。 */
  isComplete: boolean;
  /** `MAX_USERS` に達して打ち切った。 */
  reachedLimit: boolean;
}

interface AllUsersResult<T> {
  /** 全ページ分を連結した候補。取得完了までは途中経過を返す。 */
  users: T[];
  /** 全ページ取得が完了するまで `true`。 */
  isLoading: boolean;
  reachedLimit: boolean;
}

export const initialState = <T>(key: string): PagingState<T> => ({
  key,
  offset: 0,
  users: [],
  isComplete: false,
  reachedLimit: false,
});

/** 到着したページを既存の累積に連結する（ID 重複は先勝ちで除外）。 */
const mergePage = <T extends { id: number }>(
  prev: T[],
  page: UsersPage<T>,
): T[] => {
  if (page.offset === 0) return page.users;
  const seen = new Set(prev.map((u) => u.id));
  return [...prev, ...page.users.filter((u) => !seen.has(u.id))];
};

/**
 * 到着した 1 ページ分を取り込んで次の offset へ進める状態遷移。
 *
 * RTK Query のフックはループで呼べないため、`offset` を state に持って 1 ページずつ
 * 辿り、`has_next` が尽きた時点で完了とする。
 */
export const advance = <T extends { id: number }>(
  prev: PagingState<T>,
  page: UsersPage<T> | undefined,
  isError: boolean,
): PagingState<T> => {
  if (prev.isComplete) return prev;
  // 取得に失敗したら打ち切る（同じ offset を叩き続けないため）。
  if (isError) return { ...prev, isComplete: true };
  // offset を進めた直後のフレームは、まだ前ページを指していることがある。
  if (!page || page.offset !== prev.offset) return prev;

  const users = mergePage(prev.users, page);
  // BE が limit をクランプした場合にも追従できるよう、応答の limit を使う。
  const nextOffset = page.offset + (page.limit || PAGE_LIMIT);
  if (!page.has_next) return { ...prev, users, isComplete: true };
  if (nextOffset >= MAX_USERS) {
    return { ...prev, users, isComplete: true, reachedLimit: true };
  }
  return { ...prev, users, offset: nextOffset };
};

const toResult = <T>(
  state: PagingState<T>,
  skip: boolean,
): AllUsersResult<T> => ({
  users: state.users,
  isLoading: !skip && !state.isComplete,
  reachedLimit: state.reachedLimit,
});

/** `/api/me/follows` を `has_next` が尽きるまで辿り、フォロー中の全員を返す。 */
const useAllFollows = (skip: boolean): AllUsersResult<FollowUser> => {
  const [state, setState] = useState<PagingState<FollowUser>>(() =>
    initialState("follows"),
  );

  const { data, isError } = useGetFollowsQuery(
    { limit: PAGE_LIMIT, offset: state.offset },
    { skip },
  );
  const page = data?.data;

  useEffect(() => {
    if (skip) return;
    setState((prev) => advance(prev, page, isError));
  }, [page, isError, skip]);

  return toResult(state, skip);
};

type RoleUserVariant = "following" | "accepting_offers";

/**
 * `/api/roles/<role_id>/users` を `has_next` が尽きるまで辿る。
 * この API は `following` / `accepting_offers` を AND 結合するため、送信先候補は
 * 2 系統を別々に取得して呼び出し側でマージする。
 */
const useAllRoleUsers = (
  roleId: number | undefined,
  variant: RoleUserVariant,
): AllUsersResult<RoleUserItem> => {
  const skip = roleId === undefined;
  const key = `${roleId ?? "none"}:${variant}`;
  const [state, setState] = useState<PagingState<RoleUserItem>>(() =>
    initialState(key),
  );

  // 対象ロールが変わったら最初のページからやり直す。レンダリング中に巻き戻すこと
  // で、古い offset のまま新しいロールへリクエストが飛ぶのを防ぐ。
  if (state.key !== key) {
    setState(initialState(key));
  }

  const { data, isError } = useGetRoleUsersQuery(
    {
      role_id: roleId ?? 0,
      following: variant === "following",
      accepting_offers: variant === "accepting_offers",
      limit: PAGE_LIMIT,
      offset: state.key === key ? state.offset : 0,
    },
    { skip },
  );
  const page = data?.data;

  useEffect(() => {
    if (skip) return;
    setState((prev) => advance(prev, page, isError));
  }, [page, isError, skip]);

  return toResult(state, skip);
};

/**
 * 2 つのユーザーリストを「先頭リスト優先・ID 重複除外」でマージする。
 * 「フォロー中 → accepting_offers」順で使う想定。
 */
const mergeUniqueById = <T extends { id: number }>(
  primary: readonly T[],
  secondary: readonly T[],
): T[] => {
  const seen = new Set<number>();
  const merged: T[] = [];
  for (const list of [primary, secondary]) {
    for (const item of list) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      merged.push(item);
    }
  }
  return merged;
};

/**
 * ユーザーが検索クエリにマッチするか。表示名（日本語 / 英語）・肩書き・所属を
 * 対象に、大文字小文字と前後の空白を無視した部分一致で判定する。
 */
const matchesQuery = (user: FollowUser, normalizedQuery: string): boolean => {
  if (!normalizedQuery) return true;
  return [
    user.display_name_jp,
    user.display_name_en,
    user.title_jp,
    user.title_en,
    user.affiliation_jp,
    user.affiliation_en,
  ].some((field) => (field ?? "").toLowerCase().includes(normalizedQuery));
};

export interface RecipientCandidatesResult {
  /** 除外・検索フィルタを適用した送信先候補。 */
  candidates: FollowUser[];
  /** 検索フィルタを適用する前の候補数（母集団の全件）。 */
  totalCount: number;
  isLoading: boolean;
  reachedLimit: boolean;
}

/**
 * オファー送信先の候補を返す。
 *
 * 候補の母集団は 2 通り:
 *  - `roleFilterRoleId` 未指定（通常モード）: 自分がフォロー中のユーザー全員
 *  - 指定あり（ロール先モード）: そのロールを持つ「フォロー中」+「オファー受付中」
 *
 * いずれも `has_next` が尽きるまで全ページを取得してから返すため、検索は母集団の
 * 全員が対象になる（BE の `q` は名前しか見ず、ロール×ユーザー API には `q` 自体が
 * 無いので、検索はここでクライアント側に畳む）。
 */
export const useRecipientCandidates = ({
  roleFilterRoleId,
  query,
}: {
  roleFilterRoleId?: number;
  query: string;
}): RecipientCandidatesResult => {
  const isRoleFiltered = roleFilterRoleId !== undefined;

  const follows = useAllFollows(isRoleFiltered);
  const roleFollowing = useAllRoleUsers(roleFilterRoleId, "following");
  const roleAccepting = useAllRoleUsers(roleFilterRoleId, "accepting_offers");

  // ログインユーザー自身を候補から除外するための ID。
  const { data: currentUserData } = useGetCurrentUserQuery();
  const currentUserId: number | undefined = currentUserData?.data?.id;

  // `RoleUserItem extends FollowUser` のため、両 API の users は `FollowUser` 集合
  // としてそのまま並べられる（表示で必要なフィールドはすべて FollowUser 側にある）。
  //
  // is_following の補正:
  //  - `/api/me/follows`（通常モード）は「自分のフォロー中一覧」を返す API なので、
  //    レスポンスに `is_following` が欠落／false で来ていてもこの一覧上の全員は
  //    フォロー中とみなす（カード右上の bookmark アイコン点灯条件）。
  //  - ロール先絞り込みモードはフォロー外ユーザー（accepting_offers）も含むため、
  //    `roleFollowing` 由来のユーザーのみ `is_following: true` で上書きし、
  //    `roleAccepting` 由来は API 値を尊重する。
  const population: FollowUser[] = useMemo(() => {
    let list: FollowUser[];
    if (!isRoleFiltered) {
      list = follows.users.map((u) => ({ ...u, is_following: true }));
    } else {
      const followingUsers = roleFollowing.users.map((u) => ({
        ...u,
        is_following: true,
      }));
      list = mergeUniqueById<FollowUser>(followingUsers, roleAccepting.users);
    }
    // ログインユーザー自身は送信先候補に含めない（フォロー外/フォロー中いずれも）。
    if (currentUserId !== undefined) {
      list = list.filter((u) => u.id !== currentUserId);
    }
    // 管理者 / クライアント / マネージャーは送信先候補に出さない（CREATOR のみ表示）。
    return list.filter((u) => !NON_RECIPIENT_ROLES.has(u.role));
  }, [
    isRoleFiltered,
    follows.users,
    roleFollowing.users,
    roleAccepting.users,
    currentUserId,
  ]);

  const candidates = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return population;
    return population.filter((u) => matchesQuery(u, normalizedQuery));
  }, [population, query]);

  return {
    candidates,
    totalCount: population.length,
    isLoading: isRoleFiltered
      ? roleFollowing.isLoading || roleAccepting.isLoading
      : follows.isLoading,
    reachedLimit: isRoleFiltered
      ? roleFollowing.reachedLimit || roleAccepting.reachedLimit
      : follows.reachedLimit,
  };
};

export default useRecipientCandidates;
